import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { FlexBox, Icon } from '@dixa/lib-shared-habla';
import { useEffect } from 'react';
import { companiesApi } from 'apis/companies';
import { analytics } from 'app/utils/segment';
import { numberFormatterInternational } from 'app/utils/telephonyHelpers';
import { normalizeUser } from 'app/views/layout/views/contacts/utils';
import { AppLink } from 'components/appLink';
import { ContactInfo } from '../../../contactInfo/contactInfo';
import { ContactCallAction } from './components/callAction/contactCallAction';
import { ContactEmailAction } from './components/emailAction/contactEmailAction';
import { MoreDropdownAction } from './components/moreDropdownAction/moreDropdownAction';
import styles from './userInfo.module.scss';
export function UserInfo({ user }) {
    const { company_id, external_id } = user;
    const { roleAttributes, name, phone, email, additionalEmails, additionalPhoneNumbers, } = normalizeUser(user);
    const [getCompany, { data: companyResponse }] = companiesApi.useLazyReadCompanyQuery();
    useEffect(() => {
        if (company_id) {
            getCompany({ id: company_id });
        }
    }, [company_id, getCompany]);
    const relatedCompanyName = companyResponse === null || companyResponse === void 0 ? void 0 : companyResponse.data.name;
    const userRoleName = roleAttributes === null || roleAttributes === void 0 ? void 0 : roleAttributes.prettyName;
    const primaryLabel = name;
    const relatedCompanyLink = company_id ? (_jsx(AppLink, Object.assign({ path: "/contacts/company/:id", params: { path: { id: company_id } }, onClick: () => analytics.contactsCardViewCompanyClicked() }, { children: relatedCompanyName }))) : undefined;
    const secondaryLabel = [relatedCompanyLink, userRoleName]
        .filter(Boolean)
        .map((label, index) => (_jsxs("span", Object.assign({ className: styles.secondaryLabelItem }, { children: [label === relatedCompanyLink ? _jsx(Icon, { name: "building", size: 16 }) : '', _jsx("span", { children: label })] }), index)));
    /** Gets formatted phone number or null in case the user doesn't have a phone. */
    const getPhoneNumberFormat = (userPhoneNumber) => {
        const phoneNumber = userPhoneNumber === null || userPhoneNumber === void 0 ? void 0 : userPhoneNumber.trim();
        if (!phoneNumber)
            return '';
        return phoneNumber.startsWith('+')
            ? numberFormatterInternational({ phoneNumber })
            : phoneNumber;
    };
    const contactItems = [];
    if (email) {
        contactItems.push({
            user,
            label: 'Email',
            address: email,
            addressHref: `mailto:${user.email}`,
            addressTooltip: 'Compose email',
            icon: 'mail',
        });
    }
    if (additionalEmails) {
        additionalEmails.forEach((additionalEmail) => {
            contactItems.push({
                user,
                label: 'Email',
                address: additionalEmail,
                addressHref: `mailto:${additionalEmail}`,
                addressTooltip: 'Compose email',
                icon: 'mail',
            });
        });
    }
    if (phone) {
        contactItems.push({
            user,
            label: 'Phone',
            address: phone,
            addressHref: `tel:${phone}`,
            addressTooltip: 'Call',
            addressFormat: getPhoneNumberFormat,
            icon: 'phone',
        });
    }
    if (additionalPhoneNumbers) {
        additionalPhoneNumbers.forEach((additionalPhoneNumber) => {
            contactItems.push({
                user,
                label: 'Phone',
                address: additionalPhoneNumber,
                addressHref: `tel:${additionalPhoneNumber}`,
                addressTooltip: 'Call',
                addressFormat: getPhoneNumberFormat,
                icon: 'phone',
            });
        });
    }
    if (external_id) {
        contactItems.push({
            user,
            label: 'External ID',
            address: external_id,
            addressTooltip: '',
            icon: 'id-card',
        });
    }
    const actions = (_jsxs(FlexBox, Object.assign({ gap: "200", alignItems: "center" }, { children: [phone && _jsx(ContactCallAction, { user: user }), email && _jsx(ContactEmailAction, { user: user }), _jsx(MoreDropdownAction, { user: user })] })));
    return (_jsx(ContactInfo, { avatar: {
            id: user.id,
            avatar_url: user.avatar_url,
            name: user.name,
            phone_number: user.phone_number,
        }, primaryLabel: primaryLabel, secondaryLabel: secondaryLabel, contactItems: contactItems, actions: actions }));
}
